import type { Icon } from "@phosphor-icons/react";
import { GearSixIcon } from "@phosphor-icons/react/dist/csr/GearSix";
import { QuestionIcon } from "@phosphor-icons/react/dist/csr/Question";
import { getHelpTopicPath, HELP_PATH } from "@/help/routes";
import type { HelpTopic } from "@/help/topics";
import type { NavGroupId, SidebarSection } from "./nav-items";
import { helpTopicIcon } from "./rail-row-icons";
import type { NavItem } from "./use-nav-items";
import type { SettingsNav } from "./use-settings-nav";

export interface RailEntry {
  id: string;
  label: string;
  icon: Icon;
  isActive: boolean;
  to: string;
  sections: SidebarSection[];
  sourceHrefs: string[];
  onNavigate?: () => void;
}

export interface RailCluster {
  id: NavGroupId;
  entries: RailEntry[];
}

export interface RailRowGroup {
  label?: string;
  rows: SidebarSection[];
}

const fromItem = (item: NavItem): RailEntry => ({
  id: item.href,
  label: item.label,
  icon: item.icon,
  isActive: item.isActive,
  to: item.to,
  sections: item.sections,
  sourceHrefs: [item.href],
});

export const buildProductRail = (items: NavItem[]): RailCluster[] => {
  const clusters: RailCluster[] = [];
  for (const id of ["referrals", "operations"] as const) {
    const entries = items.filter((item) => item.group === id).map(fromItem);
    if (entries.length > 0) clusters.push({ id, entries });
  }
  return clusters;
};

export const buildSettingsRail = (
  settings: SettingsNav | null,
): RailEntry | null => {
  const first = settings?.items[0];
  if (!settings || !first) return null;
  return {
    id: "settings",
    label: "Settings",
    icon: GearSixIcon,
    isActive: settings.isActive,
    to: first.href,
    sourceHrefs: [],
    onNavigate: () => settings.onNavigate(),
    sections: settings.items.map((item) => ({
      label: item.title,
      to: item.href,
      isActive: item.isActive,
      icon: item.icon,
      group: item.group,
    })),
  };
};

const helpSections = (
  topics: HelpTopic[],
  currentId: string,
  group?: string,
  product?: HelpTopic["product"],
): SidebarSection[] =>
  topics.flatMap((topic) => {
    const nextProduct = topic.product ?? product;
    const nextGroup = topic.isSection ? topic.title : group;
    const children = helpSections(
      topic.children ?? [],
      currentId,
      nextGroup,
      nextProduct,
    );
    if (topic.isSection) return children;
    return [
      {
        label: topic.title,
        to: getHelpTopicPath(topic.id),
        isActive: currentId === topic.id,
        icon: helpTopicIcon(nextProduct),
        group,
      },
      ...children,
    ];
  });

export const buildHelpRail = (
  topics: HelpTopic[],
  currentId: string,
  isActive: boolean,
): RailEntry => {
  const sections = helpSections(topics, currentId);
  return {
    id: "help",
    label: "Help",
    icon: QuestionIcon,
    isActive,
    to: sections[0]?.to ?? HELP_PATH,
    sourceHrefs: [],
    sections: sections.length > 1 ? sections : [],
  };
};

export const railRowGroups = (rows: SidebarSection[]): RailRowGroup[] => {
  const groups: RailRowGroup[] = [];
  for (const row of rows) {
    const last = groups.at(-1);
    if (last && last.label === row.group) last.rows.push(row);
    else groups.push({ label: row.group, rows: [row] });
  }
  // A lone heading says nothing, unless rows above it stand apart from it
  // (Job Costing's All agents and New agent over its Agents).
  const named = groups.filter((group) => group.label);
  if (named.length > 1 || (named.length === 1 && groups.length > 1))
    return groups;
  return groups.map((group) => ({ rows: group.rows }));
};

export const railHasAttention = (entry: RailEntry) =>
  entry.sections.some((section) => section.warning);

export const previewPin = (entry: RailEntry): "top" | "bottom" =>
  entry.id === "settings" || entry.id === "help" ? "bottom" : "top";
